import React from 'react';
import Head from 'next/head';
import { useRouter } from 'next/router';

const defaultDescription: Record<LangType, string> = {
  uz: '🔥 Mavzuli testlar, Bepul DTM diognostika testlari, Avto Testlar, Xususiy Universitet kirish imthonlari va Ta’lim Yangiliklari!',
  ru: '🔥 Тематические тесты, Бесплатные диагностические тесты DTM, Авто тесты, Вступительные экзамены в частные университеты и Образовательные новости!'
};

const defaultKeywords: Record<LangType, string> = {
  uz: 'test ishlash, dtm test, dtm test ishlash, matematika test, ona tili test, ingliz tili test, online test yechish, dtm online test topshirish, dtm test pdf, dtm test ingliz tili, dtm blok test, 5 blokli test topshirish, dtm matematika test, online testlar, online test, xususiy universitetlar, xususiy universitetlar kontrakt narxlari, toshkentdagi xususiy universitetlar, xususiy universitet, nordik, umft, ',
  ru: 'решать тесты, тесты ДТМ, решать тесты ДТМ, тесты по математике, тесты по родному языку, тесты по английскому языку, решать тесты онлайн, сдача онлайн тестов ДТМ, тесты ДТМ в формате PDF, тесты ДТМ по английскому языку, блочные тесты ДТМ, сдача тестов с 5 блоками, тесты ДТМ по математике, онлайн тесты, тесты онлайн, частные университеты, частные университеты контрактные цены, частные университеты в Ташкенте, частный университет, скандинавские страны, umft,'
};

const defaultTitle: Record<LangType, string> = {
  uz: 'Akam.uz - Bilimingni Sinab Ko’r!',
  ru: 'Akam.uz - Проверь свои знания!'
};

interface IProps {
  title?: string;
  keywords?: string;
  description?: string;
  children?: React.ReactNode;
  banner?: string;
  hasCanonical?: boolean;
}

type LangType = 'uz' | 'ru';

const NextHead: React.FC<IProps> = ({ title, description, keywords, children, banner, hasCanonical = false }) => {
  const { locale = 'uz', asPath } = useRouter();
  const baseUrl = `${process.env.NEXT_PUBLIC_FRONT_URL}${asPath}`;
  const currentLang = (locale as LangType) || 'uz';

  const canonicalUrl =
    locale === 'uz'
      ? `${process.env.NEXT_PUBLIC_FRONT_URL}${decodeURI(asPath?.split('?')[0])}`
      : `${process.env.NEXT_PUBLIC_FRONT_URL}/${locale}${decodeURI(asPath?.split('?')[0])}`;

  return (
    <Head>
      <meta charSet="UTF-8" />
      <title>{title || defaultTitle[currentLang]}</title>
      {!hasCanonical && (
        <>
          <link rel="canonical" href={canonicalUrl} />
          <link
            rel="alternate"
            href={`${process.env.NEXT_PUBLIC_FRONT_URL}${decodeURI(asPath?.split('?')[0])}`}
            hrefLang="uz"
          />
          <link
            rel="alternate"
            href={`${process.env.NEXT_PUBLIC_FRONT_URL}/ru${decodeURI(asPath?.split('?')[0])}`}
            hrefLang="ru"
          />
          <link
            rel="alternate"
            href={`${process.env.NEXT_PUBLIC_FRONT_URL}${decodeURI(asPath?.split('?')[0])}`}
            hrefLang="x-default"
          />
        </>
      )}
      <meta name="theme-color" content="#00A389" />
      <link rel="apple-touch-icon" href="/favicon.ico" />
      <link rel="shortcut icon" href="/favicon.ico" type="image/x-icon" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />

      <meta name="robots" content="index, follow" />
      <meta name="keywords" content={keywords || defaultKeywords[currentLang]} />
      <meta name="description" content={description || defaultDescription[currentLang]} />

      <meta property="og:type" content="website" />
      <meta property="og:title" content={title || defaultTitle[currentLang]} />
      <meta property="og:description" content={description || defaultDescription[currentLang]} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content={title || defaultTitle[currentLang]} />
      <meta property="og:locale" content={description || defaultDescription[currentLang]} />
      <meta property="og:image" content={banner || '/images/banner.webp'} />

      <meta name="twitter:image" content={banner || '/images/banner.webp'} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta property="twitter:title" content={title || defaultTitle[currentLang]} />
      <meta property="twitter:description" content={description || defaultDescription[currentLang]} />
      <meta name="twitter:url" content={baseUrl} />
      <meta name="twitter:site" content={baseUrl} />
      <meta property="twitter:creator" content="AKAM" />

      {children}
    </Head>
  );
};

export default NextHead;
